import { useState, useCallback, useMemo } from 'react';
import type { TimePeriod, AnalyticsFilters } from '../types';
import { PERIOD_OPTIONS } from '../constants';

interface UseAnalyticsFiltersResult extends AnalyticsFilters {
    setPeriod: (period: TimePeriod) => void;
    setCustomRange: (start: string, end: string) => void;
    toggleSite: (siteId: string) => void;
    selectAllSites: (siteIds: string[]) => void;
    clearSites: () => void;
    setCompare: (compare: boolean) => void;
    periodLabel: string;
}

export const useAnalyticsFilters = (): UseAnalyticsFiltersResult => {
    const [period, setPeriodState] = useState<TimePeriod>('7d');
    const [customStart, setCustomStart] = useState<string>();
    const [customEnd, setCustomEnd] = useState<string>();
    const [selectedSites, setSelectedSites] = useState<string[]>([]);
    const [compareWithPrevious, setCompare] = useState(false);

    const setPeriod = useCallback((p: TimePeriod) => {
        setPeriodState(p);
        if (p !== 'custom') {
            setCustomStart(undefined);
            setCustomEnd(undefined);
        }
    }, []);

    const setCustomRange = useCallback((start: string, end: string) => {
        setCustomStart(start);
        setCustomEnd(end);
        setPeriodState('custom');
    }, []);

    const toggleSite = useCallback((siteId: string) => {
        setSelectedSites(prev => 
            prev.includes(siteId)
                ? prev.filter(id => id !== siteId)
                : [...prev, siteId]
        );
    }, []);

    const selectAllSites = useCallback((siteIds: string[]) => {
        setSelectedSites(siteIds);
    }, []);

    const clearSites = useCallback(() => {
        setSelectedSites([]);
    }, []);

    const periodLabel = useMemo(() => {
        const option = PERIOD_OPTIONS.find(o => o.value === period);
        if (period === 'custom' && customStart && customEnd) {
            return `${new Date(customStart).toLocaleDateString('ru-RU')} — ${new Date(customEnd).toLocaleDateString('ru-RU')}`;
        }
        return option?.label ?? period;
    }, [period, customStart, customEnd]);

    return {
        period,
        customStart,
        customEnd,
        selectedSites,
        compareWithPrevious,
        setPeriod,
        setCustomRange,
        toggleSite,
        selectAllSites,
        clearSites,
        setCompare,
        periodLabel,
    };
};